/**
 * withTimeout — bound a worker round-trip in time.
 *
 * Every git/publish operation in this app is a `postMessage` to a wasm-git
 * worker. A worker that is killed (OOM in the wasm module, a throttled
 * background tab, a failed module load that never reaches `onerror`) simply
 * never answers, and a `Promise` that never settles is indistinguishable from
 * one still working: the caller's `busy` flag stays true and the button stays
 * disabled for the life of the page.
 *
 * The timeout does not cancel the worker — it cannot; it converts "hangs
 * forever" into a rejection the caller can surface and retry from. Callers
 * that own the worker should terminate it in their own `finally`.
 */
export class WorkerTimeoutError extends Error {
  readonly what: string;
  constructor(what: string, ms: number) {
    super(`${what} timed out after ${Math.round(ms / 1000)}s`);
    this.name = "WorkerTimeoutError";
    this.what = what;
  }
}

/** Generous by default: a cold clone of a large repo over S3 is legitimately
 *  slow, so this is a stuck-detector, not a latency budget. */
export const DEFAULT_WORKER_TIMEOUT_MS = 120_000;

export function withTimeout<T>(
  p: Promise<T>,
  what: string,
  ms: number = DEFAULT_WORKER_TIMEOUT_MS,
): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const guard = new Promise<never>((_, reject) => {
    timer = setTimeout(() => reject(new WorkerTimeoutError(what, ms)), ms);
  });
  // `finally` runs on both paths, so the timer never outlives the race — an
  // uncleared one would hold the tab awake and fire into nothing.
  return Promise.race([p, guard]).finally(() => {
    if (timer !== undefined) clearTimeout(timer);
  }) as Promise<T>;
}
